/**
 * VehicleDetailPage — `/fleet/vehicles/:id`. Live · GET /v1/vehicles/:id,
 * POST /v1/vehicles/:id/status. Maintenance log + odometer/service summary
 * are now backed by GET/POST /v1/vehicles/:id/maintenance.
 */
import { type FormEvent, type JSX, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '../../lib/api/client';
import {
  getVehicle,
  listVehicleMaintenance,
  recordVehicleMaintenance,
  setVehicleStatus,
  type MaintenanceLogType,
  type VehicleStatus,
} from '../../lib/api/delivery';
import { DeferredNotice, PageHeader, StatusBadge } from '../shared/StaffUI';

const LOG_TYPES: readonly MaintenanceLogType[] = [
  'INSPECTION',
  'SERVICE',
  'REPAIR',
  'FUEL',
  'INCIDENT',
  'ODOMETER',
];

export function VehicleDetailPage(): JSX.Element {
  const qc = useQueryClient();
  const { id = '' } = useParams<{ id: string }>();
  const [outcome, setOutcome] = useState<{ kind: 'ok' | 'err'; message: string } | null>(null);

  const [logType, setLogType] = useState<MaintenanceLogType>('SERVICE');
  const [logNotes, setLogNotes] = useState<string>('');
  const [logOdometer, setLogOdometer] = useState<string>('');
  const [logCostMinor, setLogCostMinor] = useState<string>('');

  const vehicle = useQuery({
    queryKey: ['fleet', 'vehicles', 'detail', id],
    queryFn: () => getVehicle(id),
    retry: false,
    enabled: id !== '',
  });

  const maintenance = useQuery({
    queryKey: ['fleet', 'vehicles', 'maintenance', id],
    queryFn: () => listVehicleMaintenance(id),
    retry: false,
    enabled: id !== '' && vehicle.data !== undefined,
    staleTime: 30_000,
  });

  const statusMutation = useMutation({
    mutationFn: (status: VehicleStatus) => setVehicleStatus(id, status),
    onSuccess: () => {
      setOutcome({ kind: 'ok', message: 'Status updated.' });
      void qc.invalidateQueries({ queryKey: ['fleet', 'vehicles'] });
    },
    onError: (err) => {
      setOutcome({ kind: 'err', message: err instanceof Error ? err.message : 'Update failed' });
    },
  });

  const recordLog = useMutation({
    mutationFn: () =>
      recordVehicleMaintenance(id, {
        logType,
        ...(logNotes.trim() !== '' && { notes: logNotes.trim() }),
        ...(logOdometer.trim() !== '' && { odometerKm: Number.parseInt(logOdometer, 10) }),
        ...(logCostMinor.trim() !== '' && { costMinor: logCostMinor.trim() }),
      }),
    onSuccess: () => {
      setLogNotes('');
      setLogOdometer('');
      setLogCostMinor('');
      void qc.invalidateQueries({ queryKey: ['fleet', 'vehicles', 'maintenance', id] });
    },
  });

  const denied =
    vehicle.isError && vehicle.error instanceof ApiError && (vehicle.error.status === 401 || vehicle.error.status === 403);
  const notFound =
    vehicle.isError && vehicle.error instanceof ApiError && vehicle.error.status === 404;

  function onLogSubmit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    recordLog.mutate();
  }

  return (
    <div className="space-y-6 max-w-3xl">
      <PageHeader
        title={vehicle.data ? `Vehicle ${vehicle.data.registration}` : 'Vehicle detail'}
        description="Full registry record for this vehicle."
        actions={
          <Link to="/fleet" className="px-3 py-2 text-sm font-medium text-nipost-green bg-nipost-green-light rounded-lg hover:bg-nipost-green hover:text-white">
            Back to fleet
          </Link>
        }
      />

      {vehicle.isPending ? (
        <p className="text-sm text-muted">Loading…</p>
      ) : denied ? (
        <DeferredNotice title="Permission required">
          The dev token doesn&apos;t carry <code className="font-mono">Delivery/readVehicle</code>.
        </DeferredNotice>
      ) : notFound ? (
        <DeferredNotice title="Not found">Vehicle <code>{id}</code> is out of scope or does not exist.</DeferredNotice>
      ) : vehicle.isError ? (
        <DeferredNotice title="Service unreachable">{(vehicle.error as Error).message}</DeferredNotice>
      ) : vehicle.data ? (
        <>
          <div className="bg-white border border-border rounded-xl p-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <div>
              <p className="text-xs text-muted">Status</p>
              <p className="mt-1"><StatusBadge status={vehicle.data.status} /></p>
            </div>
            <div>
              <p className="text-xs text-muted">Active</p>
              <p className="mt-1 text-xs">{vehicle.data.active ? 'Yes' : 'No'}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Make / Model</p>
              <p className="mt-1 text-xs text-gray-900">{vehicle.data.make} {vehicle.data.model}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Kind</p>
              <p className="mt-1 text-xs">{vehicle.data.kind}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Capacity</p>
              <p className="mt-1 text-xs">{vehicle.data.capacityKg} kg</p>
            </div>
            <div>
              <p className="text-xs text-muted">Operator</p>
              <p className="mt-1 text-xs">#{vehicle.data.operatorId}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Last odometer</p>
              <p className="mt-1 text-xs">{maintenance.data?.lastOdometerKm != null ? `${maintenance.data.lastOdometerKm} km` : '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted">Last service</p>
              <p className="mt-1 text-xs">{maintenance.data?.lastServiceAt ? new Date(maintenance.data.lastServiceAt).toLocaleDateString() : '—'}</p>
            </div>
          </div>

          <div className="bg-white border border-border rounded-xl p-5 space-y-3">
            <h3 className="font-semibold text-gray-900 text-sm">Change status</h3>
            <div className="flex flex-wrap gap-2">
              {(['ACTIVE', 'MAINTENANCE', 'RETIRED'] as readonly VehicleStatus[]).map((s) => (
                <button
                  key={s}
                  type="button"
                  disabled={statusMutation.isPending || vehicle.data?.status === s}
                  onClick={() => statusMutation.mutate(s)}
                  className="px-3 py-2 text-xs font-semibold border border-border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Set {s}
                </button>
              ))}
            </div>
            {outcome ? (
              <p className={outcome.kind === 'ok' ? 'text-xs text-emerald-700' : 'text-xs text-red-700'}>
                {outcome.message}
              </p>
            ) : null}
          </div>

          <div className="bg-white border border-border rounded-xl p-5 space-y-4">
            <h3 className="font-semibold text-gray-900 text-sm">Maintenance log</h3>

            <form onSubmit={onLogSubmit} className="grid grid-cols-1 sm:grid-cols-4 gap-2 items-end">
              <div className="sm:col-span-1">
                <label className="block text-[10px] uppercase tracking-widest text-muted mb-1">Type</label>
                <select
                  value={logType}
                  onChange={(e) => setLogType(e.target.value as MaintenanceLogType)}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded"
                  data-testid="log-type"
                >
                  {LOG_TYPES.map((t) => (
                    <option key={t} value={t}>{t}</option>
                  ))}
                </select>
              </div>
              <div className="sm:col-span-1">
                <label className="block text-[10px] uppercase tracking-widest text-muted mb-1">Odometer (km)</label>
                <input
                  type="number"
                  value={logOdometer}
                  onChange={(e) => setLogOdometer(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded"
                  data-testid="log-odometer"
                />
              </div>
              <div className="sm:col-span-1">
                <label className="block text-[10px] uppercase tracking-widest text-muted mb-1">Cost (minor)</label>
                <input
                  type="text"
                  inputMode="numeric"
                  pattern="\d*"
                  value={logCostMinor}
                  onChange={(e) => setLogCostMinor(e.target.value.replace(/\D/g, ''))}
                  placeholder="e.g. 150000"
                  className="w-full px-2 py-1.5 text-xs border border-border rounded"
                  data-testid="log-cost"
                />
              </div>
              <div className="sm:col-span-1">
                <button
                  type="submit"
                  disabled={recordLog.isPending}
                  data-testid="log-submit"
                  className="w-full px-3 py-2 text-xs font-semibold bg-nipost-green text-white rounded hover:bg-nipost-green-dark disabled:opacity-50"
                >
                  {recordLog.isPending ? 'Recording…' : 'Add log entry'}
                </button>
              </div>
              <div className="sm:col-span-4">
                <label className="block text-[10px] uppercase tracking-widest text-muted mb-1">Notes (optional)</label>
                <input
                  type="text"
                  value={logNotes}
                  onChange={(e) => setLogNotes(e.target.value)}
                  className="w-full px-2 py-1.5 text-xs border border-border rounded"
                  data-testid="log-notes"
                />
              </div>
            </form>

            {recordLog.isError ? (
              <p className="text-xs text-red-700">{(recordLog.error as Error).message}</p>
            ) : null}

            <div className="border-t border-border pt-3">
              {maintenance.isPending ? (
                <p className="text-xs text-muted">Loading log…</p>
              ) : (
                <table className="w-full text-xs" data-testid="maintenance-table">
                  <thead className="text-[10px] uppercase tracking-wider text-muted">
                    <tr>
                      <th className="text-left px-2 py-1">When</th>
                      <th className="text-left px-2 py-1">Type</th>
                      <th className="text-left px-2 py-1">Odometer</th>
                      <th className="text-left px-2 py-1">Cost (minor)</th>
                      <th className="text-left px-2 py-1">Notes</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {(maintenance.data?.items ?? []).map((row) => (
                      <tr key={row.id}>
                        <td className="px-2 py-1 text-muted">{new Date(row.performedAt).toLocaleString()}</td>
                        <td className="px-2 py-1">{row.logType}</td>
                        <td className="px-2 py-1">{row.odometerKm != null ? `${row.odometerKm} km` : '—'}</td>
                        <td className="px-2 py-1">{row.costMinor ?? '—'}</td>
                        <td className="px-2 py-1 text-muted">{row.notes ?? ''}</td>
                      </tr>
                    ))}
                    {(maintenance.data?.items.length ?? 0) === 0 ? (
                      <tr>
                        <td colSpan={5} className="text-center text-muted py-4">No maintenance entries yet.</td>
                      </tr>
                    ) : null}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </>
      ) : null}
    </div>
  );
}
